import { z } from "zod"
import { Discount } from "./discount"

export enum StandardFeeType {
  ENVIRONMENTAL = "ENVIRONMENTAL",
  CONSUMABLES = "CONSUMABLES",
  EBIKE_ADDON = "EBIKE_ADDON",
  CARGO_BIKE_ADDON = "CARGO_BIKE_ADDON",
  EMERGENCY_SERVICE = "EMERGENCY_SERVICE",
  /** Kept up to date by the backend on finished bikes that aren't picked up. */
  PARKING = "PARKING",
}

export enum PricingMode {
  FIXED = "FIXED",
  PERCENTAGE = "PERCENTAGE",
  PER_CHANGE = "PER_CHANGE",
}

// Fields shared by manual + standard fees.
const ServiceFeeBaseSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  price: z.number().nonnegative(),
  description: z.string().nullable(),
  appendByDefault: z.boolean(),
})

/**
 * Manual fees are explicitly created by the store. Their mere existence
 * means they're "active" — there is no on/off toggle, no dynamic pricing
 * mode, no percentage/min/max plumbing. Hence those fields are absent.
 */
export const ManualServiceFeeSchema = ServiceFeeBaseSchema.extend({
  standardFeeType: z.null(),
})

/**
 * Standard fees are catalog templates the store can enable/disable.
 * `isActive` and the pricing-mode plumbing only make sense here.
 */
export const StandardServiceFeeSchema = ServiceFeeBaseSchema.extend({
  standardFeeType: z.nativeEnum(StandardFeeType),
  isActive: z.boolean(),
  pricingMode: z.nativeEnum(PricingMode),
  percentageRate: z.number().positive().nullable(),
  minPrice: z.number().nonnegative().nullable(),
  maxPrice: z.number().positive().nullable(),
})

export const ServiceFeeSchema = z.union([
  ManualServiceFeeSchema,
  StandardServiceFeeSchema,
])

export const CreateServiceFeeSchema = ServiceFeeBaseSchema.omit({ id: true })
export const UpdateServiceSchema = CreateServiceFeeSchema.partial()

export const UpdateStandardFeeSchema = z.object({
  price: z.number().nonnegative().optional(),
  isActive: z.boolean().optional(),
  pricingMode: z.nativeEnum(PricingMode).optional(),
  percentageRate: z.number().positive().nullable().optional(),
  minPrice: z.number().nonnegative().nullable().optional(),
  maxPrice: z.number().positive().nullable().optional(),
})

export type ManualServiceFee = z.infer<typeof ManualServiceFeeSchema>
export type StandardServiceFee = z.infer<typeof StandardServiceFeeSchema>
export type ServiceFee = ManualServiceFee | StandardServiceFee
export type CreateServiceFee = z.infer<typeof CreateServiceFeeSchema>
export type UpdateServiceFee = z.infer<typeof UpdateServiceSchema>
export type UpdateStandardFee = z.infer<typeof UpdateStandardFeeSchema>

export function isStandardFee(fee: ServiceFee): fee is StandardServiceFee {
  return fee.standardFeeType !== null
}

export function isManualFee(fee: ServiceFee): fee is ManualServiceFee {
  return fee.standardFeeType === null
}

export interface ServiceFeeSnapshot
  extends z.infer<typeof ServiceFeeBaseSchema> {
  originalFeeId: string
  standardFeeType: StandardFeeType | null
  /**
   * The price before `priceDiscount`, which `price` is already after. Only
   * the parking fee is discounted so far; for every other fee it is `price`.
   */
  priceOriginal: number
  priceDiscount: Discount | null
  /**
   * True while the addon order that introduced this fee is still awaiting the
   * customer's answer. Such fees belong to the addon order the customer is
   * looking at, not to the already-approved part of the job.
   */
  isPendingViaAddonOrder: boolean
}
